import { Button, Descriptions, Form, Grid, Popconfirm, theme, Typography } from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DATE_FORMAT, FileUploadSpec, GENDERS, PhoneTypes } from '../../../../constants';
import { ApplicantUser, ApplicantUserPrimaryEducationalInformation, ApplicantUserUpdateInput, IdentityVerificationCaseV1CaseDetailsApplication } from '../../../../generated';
import { Country } from '../../../../types/country';
import { CountryView } from '../../../shared/country-view';
import { IdCaseFileUploadButtonWithPreviewContainer } from '../../../shared/file-upload-button-with-preview/id-case-file-upload-button-with-preview.container';
import { AzureBlobResponse } from '../../../shared/file-upload/azure-upload';
import { HealthProfessionViewContainer } from '../../../shared/health-profession-view/health-profession-view-container';
import { CertificationByApplicantLegalDisclaimer } from './certification-by-applicant-legal-disclaimer';
dayjs.extend(utc);

const { Paragraph, Text } = Typography;

const PROFILE_CREATION_PATH = '/onboarding';
const QUALIFYING_QUESTIONS_PATH = `${PROFILE_CREATION_PATH}/qualifying-questions`;
const CONTACT_DETAILS_PATH = `${PROFILE_CREATION_PATH}/contact-details`;
const EDUCATIONAL_INFORMATION_PATH = `${PROFILE_CREATION_PATH}/primary-education-details`;
const PASSPORT_DETAILS_PATH = `/passport-details`;

interface IdVerificationPreviewProps {
  applicantUserData: ApplicantUser;
  caseDetailApplicationData: IdentityVerificationCaseV1CaseDetailsApplication;
  onSave: () => void;
  onWithdrawRequest: () => void;
  countryData: Country[];
  caseId: string;
  onUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
}

const formatDate = (date: string) => {
  return dayjs(date).format(DATE_FORMAT).toUpperCase();
};

export const IdVerificationPreview: React.FC<IdVerificationPreviewProps> = (props) => {
  const [isFocus, setIsFocus] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();
  const navigate = useNavigate();
  const [form] = Form.useForm<ApplicantUserUpdateInput>();
  const screens = Grid.useBreakpoint();

  const getCountryName = (countryCode: string, countryData: Country[]) => {
    const country = countryData.find((item) => item.countryCode === countryCode);
    return country ? country.countryName : null;
  };

  let educationalDetails: DescriptionsItemType[] = [];
  const formatPrimaryEducationDetails = (education: ApplicantUserPrimaryEducationalInformation): DescriptionsItemType[] => {
    educationalDetails = [
      {
        key: education.degreeTitle?.toString(),
        label: 'Degree Title',
        children: education.degreeTitle
      },
      {
        key: `placeholder-1`,
        label: '',
        children: ''
      },
      {
        key: education.attendanceStartedAt,
        label: 'Attendance Start Date',
        children: education.attendanceStartedAt ? formatDate(education.attendanceStartedAt) : null
      },
      {
        key: education.attendanceEndedAt,
        label: 'Attendance End Date',
        children: education.attendanceEndedAt ? formatDate(education.attendanceEndedAt) : null
      },
      {
        key: education.degreeIssuedAt,
        label: 'Date Degree Issued',
        children: education.degreeIssuedAt ? formatDate(education.degreeIssuedAt) : null
      },
      {
        key: education.graduationYear?.toString(),
        label: 'Graduation Year',
        children: education.graduationYear
      },
      {
        key: education.issuingInstitution?.toString(),
        label: 'Issuing Institution',
        children: education.issuingInstitution?.entityName ?? education.otherIssuingInstitution?.name
      },
      {
        key: `placeholder-2`,
        label: '',
        children: ''
      }
    ];

    if (education?.issuingInstitution) {
      educationalDetails.push({
        key: 'issuingInstitution-address',
        label: 'Address',
        children: (
          <Paragraph>
            {education?.issuingInstitution?.issuingInstitution?.address?.addressLine1},
            <br />
            {education?.issuingInstitution?.issuingInstitution?.address?.city}, {education?.issuingInstitution?.issuingInstitution?.address?.stateOrProvince},
            <br />
            {education?.issuingInstitution?.issuingInstitution?.address?.zipOrPostalCode}{' '}
            {getCountryName(education?.issuingInstitution?.issuingInstitution?.address?.country ?? '', props.countryData)}
          </Paragraph>
        )
      });
    } else {
      educationalDetails.push({
        key: 'issuingInstitution-address',
        label: 'Address',
        children: (
          <Paragraph>
            {education?.otherIssuingInstitution?.addressLine1},
            <br />
            {education?.otherIssuingInstitution?.city}, {education?.otherIssuingInstitution?.stateOrProvince},
            <br />
            {education?.otherIssuingInstitution?.zipOrPostalCode}, {getCountryName(education?.otherIssuingInstitution?.country ?? '', props.countryData)}
          </Paragraph>
        )
      });
    }

    return educationalDetails;
  };

  const handleSaveAndContinue = async () => {
    setIsFocus(false);
    form
      .validateFields()
      .then((_values) => {
        props.onSave();
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            boundary: document.getElementById('applicant-form'),
            block:"center"
          });
        }
        console.log('Form Error', error);
      });
  };

  const createFilePreviewItem = (key: string, label: string, fileName: string) => ({
    key,
    label,
    children: <IdCaseFileUploadButtonWithPreviewContainer isShowFileName={false} title={''} buttonText={''} caseId={props.caseId} fileName={fileName} isPreviewOnly={true} />
  });

  const formFocusHandler = () => {
    setIsFocus(true);
  };

  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          minWidth: 280,
          padding: screens.xs ? '24px' : '48px',
          width: screens.xs ? '100%' : '70%',
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}
      >
        <Title level={screens.xs ? 3 : 2}>Review Application Details</Title>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          <br />
          <Descriptions
            title={<Title level={screens.xs ? 5 : 4}>General Information</Title>}
            extra={<Button onClick={() => navigate(`/applicant${QUALIFYING_QUESTIONS_PATH}`)}>Edit</Button>}
            layout={'vertical'}
            items={[
              {
                key: 'send-to',
                label: 'Recipient for the Verification Status Report',
                children: <Text strong>{props?.applicantUserData?.personalInformation?.applyingTo?.map((applyingTo: any) => `${applyingTo?.entityName}`).join(', ')}</Text>
              },
              {
                key: 'health-profession',
                label: 'Profession of Practice',
                children: (
                  <Text strong>
                    <HealthProfessionViewContainer
                      displayValueOnly={true}
                      layout="vertical"
                      label=""
                      healthProfessionCode={props?.applicantUserData?.personalInformation?.professional?.healthProfession ?? ''}
                    />
                  </Text>
                )
              }
            ]}
          />
          <div
            style={{
              width: '100%',
              textAlign: 'justify'
            }}
          >
            <br />
            <br />
            <Descriptions
              title={<Title level={screens.xs ? 5 : 4}>Contact Details</Title>}
              extra={<Button onClick={() => navigate(`/applicant${CONTACT_DETAILS_PATH}`)}>Edit</Button>}
              layout={'vertical'}
              labelStyle={{ fontSize: '13px' }}
              items={[
                {
                  key: 'last-name',
                  label: 'Last Name (Surname/Family Name)',
                  children: props.applicantUserData.personalInformation?.identityDetails?.lastName
                },
                {
                  key: 'rest-of-name',
                  label: 'Rest of Name',
                  children: props.applicantUserData.personalInformation?.identityDetails?.restOfName
                },
                {
                  key: 'generational-suffix',
                  label: 'Generational Suffix',
                  children: props.applicantUserData.personalInformation?.identityDetails?.generationalSuffix ?? 'N/A'
                }
              ]}
            />
            <Descriptions
              layout={'vertical'}
              labelStyle={{ fontSize: '13px' }}
              items={[
                {
                  key: 'email',
                  label: 'Email Address',
                  children: props.applicantUserData.personalInformation?.contactInformation?.emailAddress
                },
                {
                  key: 'gender',
                  label: 'Gender',
                  children: GENDERS.find((gender) => gender.value === props.applicantUserData.personalInformation?.identityDetails?.gender)?.label ?? ''
                },
                {
                  key: 'date-of-birth',
                  label: 'Date of Birth',
                  children: formatDate(props.applicantUserData.personalInformation?.identityDetails?.dateOfBirth)
                }
              ]}
            />
            <Descriptions
              size="small"
              layout="vertical"
              items={[
                {
                  key: 'address',
                  label: 'Address',
                  labelStyle: { fontSize: '13px' },
                  children: (
                    <Paragraph>
                      {props.applicantUserData?.personalInformation?.contactInformation?.address?.streetAddressLine1},
                      {props.applicantUserData?.personalInformation?.contactInformation?.address?.streetAddressLine2 && (
                        <>
                          <br />
                          {props.applicantUserData?.personalInformation?.contactInformation?.address?.streetAddressLine2},
                        </>
                      )}
                      <br />
                      {props.applicantUserData?.personalInformation?.contactInformation?.address?.city && (
                        <>{props.applicantUserData?.personalInformation?.contactInformation?.address?.city},</>
                      )}{' '}
                      {props.applicantUserData?.personalInformation?.contactInformation?.address?.stateOrProvince}
                      ,
                      <br />
                      {props.applicantUserData?.personalInformation?.contactInformation?.address?.zipOrPostalCode && (
                        <>{props.applicantUserData?.personalInformation?.contactInformation?.address?.zipOrPostalCode},</>
                      )}{' '}
                      {getCountryName(props.applicantUserData?.personalInformation?.contactInformation?.address?.country ?? '', props.countryData)}
                    </Paragraph>
                  )
                },
                {
                  key: 'type',
                  labelStyle: { fontSize: '13px' },
                  label: 'Phone Type',
                  children: 'Cell (Primary)'
                },
                {
                  key: 'phone',
                  label: 'Phone Number',
                  labelStyle: { fontSize: '13px' },
                  children: props.applicantUserData.personalInformation?.contactInformation?.primaryPhone
                }
              ]}
            />
            {props.applicantUserData.personalInformation?.contactInformation?.phone?.map((phone: any, i: number) => {
              return (
                <Descriptions
                  key={phone?.phoneNumber}
                  layout="vertical"
                  labelStyle={{ fontSize: '13px' }}
                  items={[
                    {
                      key: `type-${i}`,
                      label: 'Phone Type',
                      children: PhoneTypes.find((p) => p.value === phone?.phoneType)?.label
                    },
                    {
                      key: `phone-${i}`,
                      span: 2,
                      label: 'Phone Number',
                      children: phone?.phoneNumber
                    }
                  ]}
                />
              );
            })}
            <br />
            <CountryView
              title={<Title level={4}>Citizenship Details</Title>}
              layout={'vertical'}
              extra={<Button onClick={() => navigate(`/applicant${CONTACT_DETAILS_PATH}`)}>Edit</Button>}
              countryCode={(props.applicantUserData.personalInformation?.citizenship?.currentCitizenOf ?? ['']).filter((code): code is string => code !== null) || ''}
              label="Current Country of Citizenship"
            />
            <br />
            <CountryView
              title={<Title level={4}>Educational Information</Title>}
              layout={'vertical'}
              extra={<Button onClick={() => navigate(`/applicant${EDUCATIONAL_INFORMATION_PATH}`)}>Edit</Button>}
              countryCode={props.applicantUserData.personalInformation?.professional?.citizenshipAtTimeEnteredMedicalInstitution ?? ''}
              label="Country of Citizenship When You Entered Institution"
            />
            {props.applicantUserData.personalInformation?.professional?.primaryEducationalInformation?.map((education: any, index: number) => {
              return (
                <div key={index}>
                  {index === 0 && (
                    <Title style={screens.xs ? { fontSize: '12.5px' } : { fontSize: '18px' }}>Institution that issued your Primary Professional Credential</Title>
                  )}
                  <div
                    style={{
                      borderLeft: '1px solid',
                      borderRadius: '0px',
                      padding: '0px 10px',
                      margin: '14px 8px',
                      width: 'auto'
                    }}
                  >
                    <Descriptions
                      key={education?.degreeTitle}
                      layout={'vertical'}
                      size={screens.xs ? 'small' : 'default'}
                      labelStyle={{ fontSize: '13px' }}
                      column={2}
                      items={education ? formatPrimaryEducationDetails(education) : []}
                    />
                  </div>
                </div>
              );
            })}
            <Descriptions
              title={<Title level={screens.xs ? 5 : 4}>Passport Details</Title>}
              layout={'vertical'}
              labelStyle={{ fontSize: '13px' }}
              extra={<Button onClick={() => navigate(`/applicant/onboarding${PASSPORT_DETAILS_PATH}`)}>Edit</Button>}
              items={[
                {
                  key: 'passport-country',
                  label: 'Country that Issued Passport',
                  children: props.countryData.find((country) => country.countryCode === props.caseDetailApplicationData.passportDetails?.passportCountry)?.countryName
                }
              ]}
            />
            <Descriptions
              layout={'vertical'}
              labelStyle={{ fontSize: '13px' }}
              items={
                [
                  {
                    key: 'passport-expiration-date',
                    label: 'Passport Expiration Date',
                    children: formatDate(props.caseDetailApplicationData.passportDetails?.passportExpiresAt)
                  },
                  {
                    key: 'passport-issue-date',
                    label: 'Passport Issue Date',
                    children: formatDate(props.caseDetailApplicationData.passportDetails?.passportIssuedAt)
                  },
                  {
                    key: 'passport-number',
                    label: 'Passport Number',
                    children: props.caseDetailApplicationData.passportDetails?.passportNumber
                  },
                  createFilePreviewItem('passport-image', 'Passport Image', FileUploadSpec.Passport.fileName),
                  !props.caseDetailApplicationData.passportDetails?.isPassportExpirationDateVisible
                    ? createFilePreviewItem('passport-expiration-page', 'Passport Expiration Page', FileUploadSpec.PassportExpirationPage.fileName)
                    : null,
                  !props.caseDetailApplicationData.passportDetails?.isPassportInLatinCharacters
                    ? createFilePreviewItem('passport-translation-page', 'English Translation of Passport', FileUploadSpec.PassportTranslation.fileName)
                    : null
                ].filter(Boolean) as DescriptionsItemType[]
              }
            />
            <Descriptions
              title={<Title level={screens.xs ? 5 : 4}>Photograph</Title>}
              layout={'vertical'}
              labelStyle={{ fontSize: '13px' }}
              items={[createFilePreviewItem('photo', 'Photograph', 'photo.jpg')]}
            />

            <Form
              id="applicant-form"
              onFocus={formFocusHandler}
              form={form}
              scrollToFirstError={{
                behavior: 'smooth',
                block: 'center'
              }}
            >
              <Form.Item
                name="isAttestationComplete"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms')))
                  }
                ]}
              >
                <CertificationByApplicantLegalDisclaimer showAccountEstablishmentAttestation={true} />
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '24px 0px',
          gap: screens.xs ? '10px' : '0',
          width: screens.xs ? '100%' : '70%'
        }}
      >
        <div>
          <Popconfirm
            title={isFocus ? 'Are you sure? Changes you made will not be saved.' : null}
            onConfirm={() => navigate(`/applicant/onboarding/passport-details`)}
            disabled={!isFocus}
          >
            <Button
              onClick={() => (isFocus ? null : navigate(`/applicant/onboarding/passport-details`))}
              style={{ width: screens.xs ? '80px' : '90%', height: screens.xs ? '50px' : 'auto' }}
            >
              {'<< Back'}
            </Button>
          </Popconfirm>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: '15px'
          }}
        >
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={() => props.onWithdrawRequest()}>
            <Button danger style={{ height: screens.xs ? '50px' : 'auto' }}>
              Withdraw Request
            </Button>
          </Popconfirm>
          <Button type="primary" onClick={handleSaveAndContinue} style={{ height: screens.xs ? '50px' : 'auto' }}>
            {'Save and Continue >>'}
          </Button>
        </div>
      </div>
    </>
  );
};
